import { createContext, useContext, useEffect, useState } from "react";
import { translations } from "./translations";
import { api, setFmtLocale } from "../lib/api";
import { hasHtml, sanitizeHtml, stripHtml } from "../lib/richtext";

const LangContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("mt_lang") || "fr");
  // Textes personnalisés depuis l'admin (clé plate « lang|chemin ») — par-dessus les défauts
  const [overrides, setOverrides] = useState({});
  // Mode « page Word » (admin) : chaque texte devient cliquable et modifiable en place
  const [editMode, setEditMode] = useState(() => localStorage.getItem("mt_edit") === "1");
  useEffect(() => {
    localStorage.setItem("mt_edit", editMode ? "1" : "0");
    document.body.classList.toggle("mt-editing", editMode);
  }, [editMode]);
  // Met à jour localement un texte personnalisé après enregistrement (effet immédiat)
  const setOverride = (l, path, value) =>
    setOverrides((o) => {
      const n = { ...o };
      if (value == null) delete n[`${l}|${path}`];
      else n[`${l}|${path}`] = value;
      return n;
    });
  setFmtLocale(lang);  // synchrone au rendu : les prix basculent en même temps que la langue

  useEffect(() => {
    localStorage.setItem("mt_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    api.get("/settings/content")
      .then((r) => setOverrides(r.data?.overrides || {}))
      .catch(() => {});
  }, []);

  const applyOverrides = (base, prefix) => {
    if (typeof base === "string") return overrides[`${lang}|${prefix}`] ?? base;
    if (Array.isArray(base)) return base.map((item, i) => applyOverrides(item, `${prefix}.${i}`));
    if (base && typeof base === "object") {
      const out = {};
      for (const k of Object.keys(base)) out[k] = applyOverrides(base[k], `${prefix}.${k}`);
      return out;
    }
    return base;
  };

  // Texte formaté dans l'admin (« Pages (contenu) ») → rendu HTML sécurisé
  const formatValue = (str) =>
    hasHtml(str) ? <span className="rich-text" dangerouslySetInnerHTML={{ __html: sanitizeHtml(str) }} /> : str;

  const resolve = (key, vars) => {
    const raw = key.split(".").reduce((o, k) => (o != null ? o[k] : undefined), translations[lang]);
    if (raw == null) return key;
    const value = applyOverrides(raw, key);
    if (typeof value !== "string") return value;
    if (!vars) return value;
    return Object.entries(vars).reduce((s, [k, v]) => s.replace(`{${k}}`, String(v)), value);
  };

  const t = (key, vars) => {
    const v = resolve(key, vars);
    if (typeof v !== "string") return v;
    if (editMode)  // mode « page Word » (admin) : chaque texte devient cliquable en place
      return <span data-edit-path={key} className="mt-ed">{formatValue(v)}</span>;
    return formatValue(v);
  };

  // Réglages « site » (images, liens) — personnalisations SANS langue, faites en
  // mode « Modifier la page » : overrides « site|<chemin> » (ex. site|img.hero_bg)
  const site = (key) => overrides[`site|${key}`];

  // Version toujours-texte brut : liens tel:/sms:, attributs, balises <title> SEO…
  const ts = (key, vars) => {
    const v = resolve(key, vars);
    return typeof v === "string" ? stripHtml(v) : v;
  };

  // Texte codé en dur dans une page (hors translations.js) MAIS modifiable en mode
  // « Modifier la page » : tx("contact.adresse", "123 rue…") → personnalisation par
  // langue si elle existe, sinon le texte d'origine. À utiliser avec RichText path=.
  const tx = (key, fallback) => overrides[`${lang}|${key}`] ?? fallback;

  return (
    <LangContext.Provider value={{ lang, setLang, t, ts, tx, site, editMode, setEditMode, overrides, setOverride }}>
      {children}
    </LangContext.Provider>
  );
}

export const useLang = () => useContext(LangContext);
